import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, sendRequest } from "./restUtil";
import { logError } from "services/logging";
import { apiQuery } from "utils/queries";
import { invalidateVocabularyCaches } from "services/vocabulary";
import { LocalStorageKeys } from "utils/storage";
const PREVIOUS_SEARCH_NUMBER_OF_ITEMS = 5;

export type DictionarySavedWord = {
  id: string;
  word: string;
  pos: string | null;
  notes: string | null;
  definition: string | null;
  translation: string | null;
  created: string | null;
  updated: string | null;
  audio: string | null;
};

export type DictionaryMeaning = {
  definition: string;
  examples: string[];
  domain: string | null;
  pronunciation: {
    audio: string | null;
    phonetic: string | null;
  };
};

type DictionaryPos = Record<string, DictionaryMeaning[]>;

type DictionarySearchParams = {
  text: string;
};

export type DictionarySearchResponse = {
  word: string;
  provider: string;
  pos: DictionaryPos;
};

export function searchDictionary(params: DictionarySearchParams) {
  return api.get<DictionarySearchResponse, DictionarySearchParams>(
    "/dictionary/search",
    params
  );
}

export const dictionarySearchQuery = apiQuery<
  DictionarySearchResponse,
  DictionarySearchParams
>({
  key: "/dictionary/search",
  retry: 1,
  staleTime: Infinity,
});

type GetPersonalDictionaryParams = {
  page?: number;
  size?: number;
  order_by?: "created" | "updated" | null;
  sort?: "asc" | "desc";
  filter?: string | null;
};

export type GetPersonalDictionaryResponse = {
  list: DictionarySavedWord[];
  total_count: number;
};

export const personalDictionaryQuery = apiQuery<
  GetPersonalDictionaryResponse,
  GetPersonalDictionaryParams
>({
  key: "/dictionary/saved",
});

type AddWordToDictionaryPayload = {
  word: string;
  pos?: string;
  notes?: string;
  definition?: string;
  translation?: string;
};

export type AddedWord = {
  word: DictionarySavedWord;
  // False for a word already saved, which the server returns unchanged.
  isNew: boolean;
};

export async function addWordToDictionary(
  data: AddWordToDictionaryPayload
): Promise<AddedWord> {
  // The undecoded response, to tell 201 (a new entry) from 200 (already saved).
  const response = await sendRequest("POST", "/dictionary/saved", { body: data });
  return {
    word: (await response.json()) as DictionarySavedWord,
    isNew: response.status === 201,
  };
}

export function useAddSavedWord() {
  const invalidateSavedWords = useSavedWordsInvalidation();
  return useMutation({
    mutationFn: addWordToDictionary,
    onSuccess: invalidateSavedWords,
    onError: logError,
  });
}

type UpdateDictionaryPayload = {
  pos?: string;
  notes?: string;
  definition?: string;
  translation?: string;
};

type UpdateDictionaryArgs = { id: string } & UpdateDictionaryPayload;

export function updateDictionary({ id, ...payload }: UpdateDictionaryArgs) {
  return api.patch<UpdateDictionaryPayload, DictionarySavedWord>(
    `/dictionary/saved/${id}`,
    payload
  );
}

// The server holds at most this many examples per word.
export const MAX_STORED_EXAMPLES = 12;

type DictionaryExamplesParams = {
  id: string;
  // Total examples wanted, not a batch size.
  count?: number;
  // Generate up to `count` when fewer are stored; a plain read never schedules anything.
  top_up?: boolean;
};

type DictionaryExamplesBody = {
  list: string[];
  // More examples are being written; poll until the list catches up.
  generating?: boolean;
  // The daily AI cap blocked topping up; the stored list still shows.
  limit_reached?: boolean;
};

export type DictionaryExamplesResponse =
  | { noContent: true; body: Partial<DictionaryExamplesBody> }
  | { noContent: false; body: DictionaryExamplesBody };

export const dictionaryExamplesQuery = apiQuery<
  DictionaryExamplesResponse,
  DictionaryExamplesParams
>({
  key: "/dictionary/examples",
  queryFn: async ({ key, params }) => {
    // The undecoded response, to tell 204 (examples still generating) from a 200 with data.
    const resp = await sendRequest("GET", key, { query: params });
    if (resp.status === 204) {
      return { noContent: true, body: {} };
    }
    return { noContent: false, body: (await resp.json()) as DictionaryExamplesBody };
  },
  // Examples change only through a top-up (a new key) or while generating, which
  // the interval covers; a revisit reads the cache.
  staleTime: Infinity,
  refetchInterval: query => {
    const data = query.state.data;
    const waiting = data?.noContent || data?.body.generating;
    // Nothing server-side ends a generation whose worker died, so the client
    // gives up on its own after about three minutes.
    return waiting && query.state.dataUpdateCount < 60 ? 3000 : false;
  },
  retry: false,
});

export function deleteWordFromDictionary(id: string) {
  return api.delete(`/dictionary/saved/${id}`);
}

export function useDeleteSavedWord() {
  const invalidateSavedWords = useSavedWordsInvalidation();
  return useMutation({
    mutationFn: deleteWordFromDictionary,
    onSuccess: invalidateSavedWords,
    onError: logError,
  });
}

// Adding or deleting a word invalidates every cache that renders the saved-word
// corpus: the drawer's list pages and the vocabulary page's own endpoints. The
// invalidation is deliberately not returned, so a caller's own onSuccess runs
// without waiting for the refetch.
function useSavedWordsInvalidation() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({
      queryKey: personalDictionaryQuery.baseKey,
    });
    invalidateVocabularyCaches(queryClient);
  };
}

export function addItemToPreviousSearch(item: string) {
  let previousSearch = getPreviousSearch();

  if (previousSearch.includes(item)) {
    return previousSearch;
  }

  previousSearch = [...previousSearch, item];
  if (previousSearch.length > PREVIOUS_SEARCH_NUMBER_OF_ITEMS) {
    previousSearch = previousSearch.slice(-PREVIOUS_SEARCH_NUMBER_OF_ITEMS);
  }

  localStorage.setItem(
    LocalStorageKeys.dictionaryPreviousSearch,
    JSON.stringify(previousSearch)
  );
  return previousSearch;
}

export function getPreviousSearch(): string[] {
  const previousSearch = localStorage.getItem(LocalStorageKeys.dictionaryPreviousSearch);
  if (!previousSearch) {
    return [];
  }

  const parsed = JSON.parse(previousSearch) as unknown;
  if (!Array.isArray(parsed)) {
    return [];
  }

  return parsed.filter((item): item is string => typeof item === "string");
}
